/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { yearToCropYearShort, OLAPTransformerResult, castUnit, GLOBAL_UNIT } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { orderBy } from "lodash";
import { CATEGORIES_AXIS, SERIES_AXIS } from "../../Report6/constants";

type TransformData = {
  areaHarvestedByYear: OLAPTransformerResult | null;
  productionByYear: OLAPTransformerResult | null;
  yieldByYear: OLAPTransformerResult | null;
  yieldByYearCurrentForecast: OLAPTransformerResult | null;
  orderedCountriesIds: number[] | null;
};

export function useChart1(data: TransformData): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const { areaHarvestedByYear, productionByYear, yieldByYear, yieldByYearCurrentForecast, orderedCountriesIds } = data;

  const categories = useMemo(
    () =>
      productionByYear &&
      orderBy(productionByYear.columns, (c) => orderedCountriesIds?.indexOf(Number(c.selection[CATEGORIES_AXIS]))).map(
        (x) => x.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name || "",
      ),
    [productionByYear],
  );

  const getPointHint = (rowIndex: number, pointIndex: number) => {
    const year = productionByYear?.rows[rowIndex].attributes[`${SERIES_AXIS}.name`];
    const yearId = Number(productionByYear?.rows[rowIndex].selection[`${SERIES_AXIS}`]);
    const cropYear = yearToCropYearShort(year);

    const productionPoints = orderBy(productionByYear?.rows[rowIndex]?.points, (p) => {
      return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
    });
    const production = {
      title: `${cropYear} Production`,
      value:
        castUnit(productionPoints[pointIndex]?.value, GLOBAL_UNIT.PRODUCTION_MMT, GLOBAL_UNIT.DISPLAY_PRODUCTION) ||
        0,
    };

    const yieldPoints = orderBy(
      [yieldByYear, yieldByYearCurrentForecast].filter(Boolean).flatMap(x => x!.rows)
      .find(row => row.selection[`${SERIES_AXIS}`] === yearId)?.points || [], (p) => {
      return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
    });
    const yieldHint = {
      title: `${cropYear} Yield`,
      value: castUnit(yieldPoints[pointIndex]?.value, GLOBAL_UNIT.YIELD_MTHA, GLOBAL_UNIT.DISPLAY_YIELD) || 0,
    };

    const areaHarvestedPoints = orderBy(areaHarvestedByYear?.rows[rowIndex]?.points, (p) => {
      return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
    });
    const areaHarvested = {
      title: `${cropYear} Area Harvested`,
      value: castUnit(areaHarvestedPoints[pointIndex]?.value, GLOBAL_UNIT.AREA_MHA, GLOBAL_UNIT.DISPLAY_AREA) || 0,
    };

    return [yieldHint, production, areaHarvested];
  };

  const getYieldPointHint = (rowSelection: string, pointIndex: number) => {
    const cropYear = yearToCropYearShort(rowSelection);

    const productionPoints = orderBy(
      productionByYear?.rows.find((row) => row.attributes[`${SERIES_AXIS}.name`] === rowSelection)?.points,
      (p) => {
        return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
      },
    );
    const production = {
      title: `${cropYear} Production`,
      value:
        castUnit(productionPoints[pointIndex]?.value, GLOBAL_UNIT.PRODUCTION_MMT, GLOBAL_UNIT.DISPLAY_PRODUCTION) ||
        0,
    };

    const yieldPoints = orderBy(
      [yieldByYear, yieldByYearCurrentForecast].filter(Boolean).flatMap(x => x!.rows).find((row) => row.attributes[`${SERIES_AXIS}.name`] === rowSelection)?.points,
      (p) => {
        return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
      },
    );
    const yieldHint = {
      title: `${cropYear} Yield`,
      value: castUnit(yieldPoints[pointIndex]?.value, GLOBAL_UNIT.YIELD_MTHA, GLOBAL_UNIT.DISPLAY_YIELD) || 0,
    };

    const areaHarvestedPoints = orderBy(
      areaHarvestedByYear?.rows.find((row) => row.attributes[`${SERIES_AXIS}.name`] === rowSelection)?.points,
      (p) => {
        return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
      },
    );
    const areaHarvested = {
      title: `${cropYear} Area Harvested`,
      value: castUnit(areaHarvestedPoints[pointIndex]?.value, GLOBAL_UNIT.AREA_MHA, GLOBAL_UNIT.DISPLAY_AREA) || 0,
    };

    return [yieldHint, production, areaHarvested];
  };

  const options = useChartOptions({}, [
    chartPipe("column"),
    legendPipe("top", {
      labelFormatter: function() {
        const { name } = this;
        switch (name) {
          case "Production":
            return name + " (million mt)";
          case "Area Harvested":
            return name + " (million ha)";
          case "Yield":
            return name + " (mt/ha)";
          default:
            return name;
        }
      },
    }),
    addXAxisPipe(categories),
    addYAxisPipe({
      labels: {
        formatter: function() {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
      title: {
        text: "Production million mt",
      },
    }),
    addYAxisPipe({
      opposite: true,
      labels: {
        formatter: function() {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 2);
          }
          return label;
        },
      },
      title: {
        text: "Yield mt/ha",
      },
    }),
    tooltipPipe((serie) => {
      switch (serie) {
        case "Production":
          return "million mt";
        case "Area Harvested":
          return "million ha";
        case "Yield":
          return "mt/ha";
        default:
          return "";
      }
    }),
  ]);

  useEffect(() => {
    if (areaHarvestedByYear && productionByYear && yieldByYearCurrentForecast) {
      const columnSeries1 = productionByYear.rows.map<SeriesOptionsType>((row, i, arr) => {
        return {
          type: "column",
          yAxis: 0,
          showInLegend: true,
          dataLabels: {
            enabled: i === 0 || i === arr.length - 1,
            formatter: function() {
              return Highcharts.numberFormat(this.y || 0, 0);
            },
          },
          name: yearToCropYearShort(row.attributes[`${SERIES_AXIS}.name`]),
          id: row.attributes[`${SERIES_AXIS}.name`],
          data: orderBy(row.points, (p) => {
            return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
          }).map((p, pointIndex) => ({
            y: castUnit(p.value, GLOBAL_UNIT.PRODUCTION_MMT, GLOBAL_UNIT.DISPLAY_PRODUCTION) || 0,
            attributes: { ...p.attributes, highlightType: "row" },
            hintInfo: getPointHint(i, pointIndex),
          })),
        };
      });

      const colors = ["#B40000", "rgb(0, 88, 151)"];
      const columnSeries2 = [yieldByYear, yieldByYearCurrentForecast].flatMap(x => x && x.rows || []).map<SeriesOptionsType>((row, i) => {
        return {
          type: "line",
          yAxis: 1,
          dashStyle: "Dash",
          color: colors[i],
          showInLegend: true,
          dataLabels: {
            color: colors[i],
            enabled: true,
            formatter: function() {
              return Highcharts.numberFormat(this.y || 0, 1);
            },
          },
          lineWidth: 2,
          marker: {
            symbol: "diamond",
          },
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          name: `Yield ${yearToCropYearShort(row.attributes[`${SERIES_AXIS}.name`])}`,
          id: row.attributes[`${SERIES_AXIS}.name`],
          data: orderBy(row.points, (p) => {
            return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
          }).map((p, pointIndex) => ({
            y: p.value || 0,
            attributes: { ...p.attributes, highlightType: "cell" },
            hintInfo: getYieldPointHint(row.attributes[`${SERIES_AXIS}.name`], pointIndex),
          })),
        };
      });

      const result: Highcharts.Options = {
        ...options,
        series: columnSeries1.concat(columnSeries2),
      };

      setState(result);
    }
  }, [areaHarvestedByYear, productionByYear, yieldByYear, yieldByYearCurrentForecast]);
  return state;
}
